import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import * as Styles from './ChatHistory.styles';
import IconImage from '@/media/MaiaIA/iconImage.png';
import { SRC } from '../../AiChat.enum';
import {
  IconThumb, Modal, IconZoom, IconOpenEye, IconDownload,
  IconDocumentFile, StepsAccordion, CopySnippet,
} from 'gatorcomponents';
import { IconButton } from '@/pages/common/v1/IconButton';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ATIVACAO_AICHAT_RATING_THUMBS } from '@/config/GrowthBook/constants';
import { useDispatch, useSelector } from 'react-redux';
import { commonActions } from '@/redux/modules';

const ChatHistory = ({
  history,
  locales,
  loadingMessage,
  loading,
  handleCloseClick,
  handleOpenSupport,
  handleRedirectToMigration,
  renderInHouseChat,
  handleRedirect,
  handleRateMessage,
  ratedMessages,
  fullScreen,
  handleSubmitMerlin,
  openDocument,
  viewFullImage,
  name,
}) => {
  const [modalContent, setModalContent] = useState(null);
  const historyListRef = useRef(null);
  const withTumbs = useFeatureIsOn(ATIVACAO_AICHAT_RATING_THUMBS);

  const { pdfUrl } = useSelector(state => state.common);
  const dispatch = useDispatch();

  const closeModal = () => {
    setModalContent(null);
  };

  useEffect(() => {
    if (historyListRef.current) {
      historyListRef.current.scrollTop = historyListRef.current.scrollHeight;
    }
  }, [history.length]);


  const mountRatings = useCallback(({ messageId, message }) => {
    const isActive = ratedMessages && ratedMessages.find(item => item.messageId === messageId && item.rating);
    const isInActive = ratedMessages && ratedMessages.find(item => item.messageId === messageId && !item.rating);

    return (
      <Styles.RatingRow>
        {((message.text || message.html) && message.src === SRC.BOT) && (
          <Styles.SnippetWrapper>
            <CopySnippet
              content={message.text || JSON.stringify(message.html)}
              iconSize="sm"
            />
          </Styles.SnippetWrapper>
        )}

        {(message.id && withTumbs) && (
          <>
            <Styles.RoundedButton
              onClick={() => handleRateMessage({ messageId, rating: true })}
              $active={isActive}
            >
              <IconThumb
                size="sm"
                color={isActive ? 'primaryMedium' : 'lowMedium'}
              />
            </Styles.RoundedButton>

            <Styles.RoundedButton
              onClick={() => handleRateMessage({ messageId, rating: false })}
              $active={isInActive}
            >
              <IconThumb
                size="sm"
                color={isInActive ? 'primaryMedium' : 'lowMedium'}
                direction="right"
              />
            </Styles.RoundedButton>
          </>
        )}
      </Styles.RatingRow>
    );
  }, [
    handleRateMessage,
    ratedMessages,
    withTumbs,
  ]);

  const maiaIconRow = () => (
    <Styles.NameRow>
      <Styles.MaiaIcon src={IconImage} />
      <Styles.AiName>{name}</Styles.AiName>
    </Styles.NameRow>
  );

  const getPdf = (pdf, type) => {
    const urlType = type === 'download' ? 'download' : '';
    dispatch(commonActions.pdf.request({ pdfUrl: pdf, urlType }));
  };


  const mountText = ({ message }) => (
    <Styles.HistoryRow data-testid="textMessage">
      {message.src === SRC.BOT && (
        maiaIconRow()
      )}

      {message.html && (
        <Styles.ResponseText $src={message.src} dangerouslySetInnerHTML={{ __html: message.html }} />
      )}

      {!message.html && (
        <Styles.ResponseText $src={message.src}>
          {message.text}
        </Styles.ResponseText>
      )}

      {mountRatings({ messageId: message.id, message })}
    </Styles.HistoryRow>
  );

  const executeAction = (action, params) => {
    const actions = {
      closeChat: () => handleCloseClick({ withTimout: false }),
      openSupport: () => handleOpenSupport({ params }),
      migration: () => handleRedirectToMigration(params),
      redirect: () => handleRedirect({ params }),
    };

    return actions[action]();
  };

  const handleClickOptions = (option) => {
    handleSubmitMerlin && handleSubmitMerlin(option);
  };

  const mountOption = ({ message, index }) => (
    <Styles.HistoryRow>
      {message.src === SRC.BOT && (
        maiaIconRow()
      )}

      <Styles.HistoryColumn>
        {message.html && (
          <Styles.ResponseText dangerouslySetInnerHTML={{ __html: message.html }} />
        )}

        {!message.html && (
          <Styles.ResponseText>
            {message.text}
          </Styles.ResponseText>
        )}

        <Styles.OptionsList>
          {message.options && message.options.map(option => (
            <Styles.OptionItem key={`option-${option}`}>
              <Styles.ButtonOption
                type="button"
                onClick={() => handleClickOptions(option)}
                disabled={index < (history.length - 1)}
              >
                {option}
              </Styles.ButtonOption>
            </Styles.OptionItem>
          ))}
        </Styles.OptionsList>
      </Styles.HistoryColumn>

      {mountRatings({ messageId: message.id, message })}
    </Styles.HistoryRow>
  );

  const mountSteps = ({
    message, index,
  }) => (
    <>
      {(message.text || message.html) && mountText({ message })}
      <Styles.HistoryRow>
        <StepsAccordion
          steps={message.options}
          isFinished={index < history.length - 1}
          timeInSeconds={3}
        />
      </Styles.HistoryRow>
    </>
  );

  const setContent = (content) => {
    setModalContent(content);
  };
  const mountImage = ({ message }) => (
    <Styles.HistoryRow>
      {message.src === SRC.BOT && (
        maiaIconRow()
      )}

      {message.images && (
      <Styles.ResponseImage>
        {message.images.map(img => (
          <Styles.ImageWrapper onClick={() => setContent(<Styles.Image $full src={img.url} alt={img.alt} />)}>

            <Styles.Image src={img.url} alt={img.alt} $full={fullScreen} />
            <Styles.Overlay>
              <Styles.OverlayText>
                <IconZoom color="highPure" />
                {viewFullImage}
              </Styles.OverlayText>
            </Styles.Overlay>
          </Styles.ImageWrapper>
        ))}
      </Styles.ResponseImage>
      )}

      {!message.html && (
      <Styles.ResponseText $src={message.src}>
        {message.text}
      </Styles.ResponseText>
      )}
    </Styles.HistoryRow>
  );

  const mountfile = ({ message }) => (
    <Styles.HistoryRow>
      <Styles.DocumentMsgWrapper>
        <Styles.DocumentTitle>
          <IconDocumentFile size="md" />
          <Styles.DocumentTitleText>{locales.marketingPlanTitle}</Styles.DocumentTitleText>
        </Styles.DocumentTitle>

        <Styles.DocumentActions>
          <IconButton
            icon={<IconOpenEye size="md" color="primaryMedium" />}
            onClick={() => openDocument({ url: message.url })}
            testId="open-document-button"
          />

          <Styles.DocumentDownload
            onClick={() => getPdf(message.filename, 'download')}
            download="plano-de-marketing.pdf"
            target="_blank"
            data-testid="download-document-button"
          >
            <IconDownload size="md" color="primaryMedium" />
          </Styles.DocumentDownload>

        </Styles.DocumentActions>
      </Styles.DocumentMsgWrapper>
    </Styles.HistoryRow>
  );

  const onDownloadPdf = useCallback((url) => {
    const link = document.createElement('a');
    link.href = url.presigned_url;
    const fileName = url.file_name;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  useEffect(() => {
    if (pdfUrl) {
      onDownloadPdf(pdfUrl);
    }
  }, [pdfUrl, onDownloadPdf]);

  const mountMessage = ({ message, index }) => {
    const rules = {
      text: mountText({ message }),
      action: () => executeAction(message.action, message.params),
      options: mountOption({ message, index }),
      steps: mountSteps({ message, index }),
      image: mountImage({ message }),
      file: mountfile({ message }),
    };

    if (typeof rules[message.type] === 'function') {
      rules[message.type]();
      return;
    }

    return rules[message.type] || null;
  };

  if (!renderInHouseChat) {
    return;
  }

  return (
    <Styles.HistoryList
      ref={historyListRef}
      $hasScroll={
        historyListRef && historyListRef.current && historyListRef.current.scrollHeight > historyListRef.current.clientHeight
      }
      $fullScreen={fullScreen}
    >
      {modalContent && (
        <Modal onClose={() => closeModal()} variant="highLight" maxWidth="990" maxHeight="600">
          {modalContent}
        </Modal>
      )}

      {history.map((message, index) => (
        <Styles.ListItem key={JSON.stringify(message)}>
          {mountMessage({ message, index })}
        </Styles.ListItem>
      ))}

      {loading && (
        <Styles.ListItem>
          <Styles.HistoryRow>
            <Styles.MaiaIcon src={IconImage} $marginTop />
            <Styles.LoadingMessage>
              {loadingMessage}
            </Styles.LoadingMessage>
          </Styles.HistoryRow>
        </Styles.ListItem>
      )}

      <div
        id="aiChatSurvey"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 0',
          maxWidth: '100%',
        }}
      />
    </Styles.HistoryList>
  );
};

export default ChatHistory;
